import React, { useState } from "react";
import Icon from "@mdi/react";
import { mdiHelpCircle, mdiHelpCircleOutline, mdiInformationOutline } from "@mdi/js";
import BaseModal from "./BaseModal";
import "./InfoHelp.css";

/**
 * InfoHelp — a label with a help icon that opens a small explanation modal, plus an
 * optional muted one-line hint under the label. Click/tap to open (never hover-only),
 * keyboard reachable, closes on Escape / outside click / "Got it" (BaseModal).
 *
 * Drop-in for a label cell: works in CSS-class rows and inline-style rows alike — pass the
 * row's label style via `labelStyle` so the label keeps that page's look.
 *
 * @param {Object} props
 * @param {React.ReactNode} props.label - visible label text
 * @param {string} [props.hint] - muted line under the label
 * @param {string} props.title - modal title
 * @param {string} props.ariaLabel - accessible name for the icon button
 * @param {React.ReactNode} props.children - modal body
 * @param {string} [props.className]
 * @param {Object} [props.labelStyle]
 */
const InfoHelp = ({ label, hint, title, ariaLabel, children, className = "", labelStyle }) => {
    const [isOpen, setIsOpen] = useState(false);
    const [isHovered, setIsHovered] = useState(false);

    return (
        <span className={`info-help ${className}`.trim()}>
            <span className="info-help-label" style={labelStyle}>
                {label}
                <button
                    type="button"
                    className="info-help-btn"
                    onClick={() => setIsOpen(true)}
                    onMouseEnter={() => setIsHovered(true)}
                    onMouseLeave={() => setIsHovered(false)}
                    aria-label={ariaLabel}
                    aria-haspopup="dialog"
                    aria-expanded={isOpen}>
                    <Icon
                        path={isHovered ? mdiHelpCircle : mdiHelpCircleOutline}
                        size={0.85}
                        color="var(--dl-color-purple-default)"
                    />
                </button>
            </span>
            {hint && <span className="info-help-hint">{hint}</span>}

            <BaseModal
                isOpen={isOpen}
                onClose={() => setIsOpen(false)}
                title={title}
                icon={mdiInformationOutline}
                maxWidth={460}
                footer={
                    <button type="button" className="base-modal-save-btn" onClick={() => setIsOpen(false)}>
                        Got it
                    </button>
                }>
                <div className="info-help-body">{children}</div>
            </BaseModal>
        </span>
    );
};

export default InfoHelp;
